import { EllipsisVertical } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/core/components/auth/store/auth";
import { getRoleName } from "@/core/utils/role";
import {
  SIDEBAR_WIDTH_MOBILE,
  SidebarFooter as SidebarFooterUI,
  useSidebar,
} from "@/core/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuTrigger,
} from "@/core/components/ui/dropdown-menu";
import {
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@radix-ui/react-dropdown-menu";
import { Sheet, SheetContent } from "@/core/components/ui/sheet";
import { WosUserMenu } from "./wos-user-menu";

function WosUserCard({ hideText }: { hideText: boolean }) {
  const { userInfo } = useAuth();
  const [translate] = useTranslation(["core"]);

  const roleKey = getRoleName(
    userInfo.profile.role[0] as Parameters<typeof getRoleName>[0],
  );

  return (
    <div className="flex items-center gap-3">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-sm font-bold text-primary">
        {userInfo.user.first_name.charAt(0)}
        {userInfo.user.last_name.charAt(0)}
      </div>
      {!hideText && (
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-primary">
            {userInfo.league?.name || userInfo.user.first_name}
          </div>
          <div className="truncate text-xs text-muted-foreground">
            {userInfo.user.first_name} {userInfo.user.last_name} •{" "}
            {translate(`${roleKey}`)}
          </div>
        </div>
      )}
    </div>
  );
}

export function WosSidebarFooter() {
  const [openMobile, setOpenMobile] = useState<boolean>(false);
  const { state, isMobile } = useSidebar();

  const hideText = state === "collapsed";

  return (
    <SidebarFooterUI
      className={`cursor-pointer border-t border-sidebar-border p-4 ${hideText ? "px-2" : ""}`}
      data-testid="wos-sidebar-footer"
    >
      <DropdownMenu>
        {isMobile ? (
          <div onClick={() => setOpenMobile(true)}>
            <WosUserCard hideText={hideText} />
          </div>
        ) : (
          <DropdownMenuTrigger asChild>
            <div className="flex items-center justify-between">
              <WosUserCard hideText={hideText} />
              {!hideText && (
                <EllipsisVertical className="size-5 shrink-0 text-primary" />
              )}
            </div>
          </DropdownMenuTrigger>
        )}

        <DropdownMenuContent
          className="min-w-56 rounded-lg"
          side="right"
          align="end"
          sideOffset={4}
        >
          <DropdownMenuLabel className="p-0 font-normal">
            <div className="px-1.5 py-1">
              <WosUserCard hideText={false} />
            </div>
          </DropdownMenuLabel>

          <DropdownMenuSeparator />

          <DropdownMenuGroup className="mt-2 flex flex-col">
            <WosUserMenu as="dropdown" />
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <Sheet open={openMobile} onOpenChange={setOpenMobile}>
        <SheetContent
          data-sidebar="sidebar"
          data-mobile="true"
          className="w-[--sidebar-width] bg-sidebar p-0 text-sidebar-foreground [&>button]:hidden"
          style={
            {
              "--sidebar-width": SIDEBAR_WIDTH_MOBILE,
            } as React.CSSProperties
          }
          side={"left"}
        >
          <div className="flex h-full w-full flex-col pt-6">
            <WosUserMenu as="sheet" />
          </div>
        </SheetContent>
      </Sheet>
    </SidebarFooterUI>
  );
}
